import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorHandlingService } from '../general/error-handling.service';
import { ISimulationDetail } from '../../interfaces/self-serve/isimulation-details-information';

import { ISectionsWithFacts } from 'src/app/interfaces/self-serve/requests/ISectionsWithFacts-update';
import { ISimulationPrompt } from '../../interfaces/self-serve/isimulation-prompt';
import { ISimulationUpdate } from 'src/app/interfaces/self-serve/requests/isimulation-update';
import { IFetchSimulationDetails } from 'src/app/interfaces/self-serve/responses/ifetch-simulation-details';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { IPersonaInfo } from 'src/app/interfaces/self-serve/assignment-manager/ipersona-info';
import { IModule } from 'src/app/interfaces/self-serve/responses/imodule';
import { CallWordType } from 'src/app/types/call-word.types';
import { ICloneModuleKeywordsRequest } from 'src/app/interfaces/self-serve/requests/iclone-module-keywords-request';
import { IGetWordDetailsResponse } from 'src/app/interfaces/self-serve/responses/iget-word-details-response';
import { IWordsRequest } from 'src/app/interfaces/self-serve/requests/iwords-request';
import { IDeleteWordsRequest } from 'src/app/interfaces/self-serve/requests/idelete-words-request';
import { ISaveAsProductDefaultRequest } from 'src/app/interfaces/self-serve/requests/isave-as-product-default-request';

export const SIMULATION_NAME_ALREADY_EXISTS_STATUS = 409;
export const DUPLICATE_WORD_STATUS = 409;

@Injectable({
  providedIn: 'root',
})
export class AuthorService {
  backendUrl: string = environment.backendUrl;

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService
  ) {}

  findAllSimulations(accountUuid: string, status?: string): Observable<any[]> {
    let url = `${this.backendUrl}/simulation/findAll?accountUuid=${accountUuid}`;
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    return this.http.get<any[]>(url).pipe(
      tap((res) => console.log('Fetched simulations:', res)),
      catchError((error) => {
        return this.errorHandlingService.handleError<any>(
          `/simulation/findAll?accountUuid=${accountUuid}${
            status ? `&status=${status}` : ''
          }`
        )(error);
      })
    );
  }

  findPublishedCallsByAccountUuid(accountUuid: string): Observable<any[]> {
    return this.http
      .get<any[]>(
        `${this.backendUrl}/call/findPublishedCallsByAccountUuid?accountUuid=${accountUuid}`
      )
      .pipe(
        tap((res) =>
          console.log('Fetched published calls by account uuid:', res)
        ),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/call/findPublishedCallsByAccountUuid?accountUuid=${accountUuid}`
          )(error);
        })
      );
  }

  getAccountList(): Observable<any> {
    return this.http
      .get<any>(`${this.backendUrl}/account/findAllAccountsByCustomer`)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`account list: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/account/findAllAccountsByCustomer`
          )(error);
        })
      );
  }

  createSimulationAPI(name: string, moduleUuid?: string): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    const body: any = {
      name,
      accountUuid,
    };
    if (moduleUuid) {
      body.moduleUuid = moduleUuid;
    }

    return this.http
      .post<any>(`${this.backendUrl}/simulation/create`, body)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`simulationCreate: ${outcome}`);
        }),
        catchError((error) => {
          if (error?.status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
            throw error;
          }
          return this.errorHandlingService.handleError<any>(
            `/simulation/create`
          )(error);
        })
      );
  }

  updateSimulation(data: ISimulationUpdate): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const updatedData = { ...data, accountUuid };

    return this.http
      .post(`${this.backendUrl}/simulation/update`, updatedData)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`update simulation: ${outcome}`);
        }),
        catchError((error) => {
          if (error?.status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
            throw error;
          }
          return this.errorHandlingService.handleError<any>(
            `/simulation/update`
          )(error);
        })
      );
  }

  deleteSimulationAPI(callUuid: string, moduleUuid: string): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    const url = `${this.backendUrl}/simulation/delete?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`;
    return this.http.delete(url).pipe(
      tap(() => {
        console.log(`Simulation ${callUuid} deleted successfully`);
      }),
      catchError((error) => {
        return this.errorHandlingService.handleError<any>(
          `/simulation/delete?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
        )(error);
      })
    );
  }

  duplicateSimulationAPI(payload: {
    callUuid: string;
    callTitle: string;
    moduleUuid: string;
  }): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const updatedData = { ...payload, accountUuid };

    return this.http
      .post(`${this.backendUrl}/simulation/duplicate`, updatedData)
      .pipe(
        tap((res) => {
          const outcome = res ? 'success' : 'failed';
          console.log(`Simulation duplicated: ${outcome}`);
        }),
        catchError((error) => {
          if (error?.status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
            throw error;
          }
          return this.errorHandlingService.handleError<any>(
            `/simulation/duplicate`
          )(error);
        })
      );
  }

  createPersonaInfo(payload: {
    mode: string;
    callUuid: string;
    personaName: string;
    replicaId: string;
    welcomeMessage: string[];
  }): Observable<any> {
    return this.http
      .post(`${this.backendUrl}/tavus/createPersona`, payload)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'success' : 'failed';
          console.log(`Persona creation: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/tavus/createPersona`
          )(error);
        })
      );
  }

  updatePersonaInfo(payload: IPersonaInfo): Observable<any> {
    return this.http
      .post(`${this.backendUrl}/tavus/updatePersona`, payload)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'success' : 'failed';
          console.log(`Persona updation: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/tavus/updatePersona`
          )(error);
        })
      );
  }

  publishSimulation(callUuid: string, moduleUuid: string): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    const url = `${this.backendUrl}/simulation/publish?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`;
    return this.http
      .get(url, {
        responseType: 'text',
      })
      .pipe(
        tap(() =>
          console.log(`Simulation published for callUuid: ${callUuid}`)
        ),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/publish?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
          )(error);
        })
      );
  }

  getAvatarReplicas(): Observable<any> {
    return this.http
      .get<any>(`${this.backendUrl}/tavus/getPublishedReplicas`)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getPublishedReplicas: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/tavus/getPublishedReplicas`
          )(error);
        })
      );
  }

  getAttempts(callUuid: string, moduleUuid: string) {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .get<any>(
        `${this.backendUrl}/simulation/viewAttempts?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`viewAttempts: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/viewAttempts?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
          )(error);
        })
      );
  }

  getUsersForTesting(accountUuid: string) {
    return this.http
      .get<any>(
        `${this.backendUrl}/user/getUsersInfoByAccount?accountUuid=${accountUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getUsersInfoByAccount: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/user/getUsersInfoByAccount?accountUuid=${accountUuid}`
          )(error);
        })
      );
  }

  sendTestAssignment(
    callUuid: string,
    userUuids: string[],
    moduleUuid: string
  ) {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post<any>(`${this.backendUrl}/simulation/sendTestAssignment`, {
        callUuid,
        userUuids,
        accountUuid,
        moduleUuid,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'posted' : 'did not post';
          console.log(`sendTestAssignment: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/sendTestAssignment?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
          )(error);
        })
      );
  }

  removeAssignment(
    callUuid: string,
    userUuid: string,
    moduleUuid: string
  ): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .delete(
        `${this.backendUrl}/simulation/removeAssignment?callUuid=${callUuid}&userUuid=${userUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        tap(() => console.log(`Assignment removed`)),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/removeAssignment?callUuid=${callUuid}&userUuid=${userUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
          )(error);
        })
      );
  }

  createSimulationDetails(
    callUuid: string,
    callDetails: ISimulationDetail[]
  ): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .post(`${this.backendUrl}/simulation/createDetails`, {
        callUuid,
        callDetails,
        accountUuid,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`create simulation details: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/createDetails`
          )(error);
        })
      );
  }

  updateSimulationDetails(callDetails: ISimulationDetail[]): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .post(`${this.backendUrl}/simulation/updateDetails`, {
        callDetails,
        accountUuid,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`update simulation details: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/updateDetails`
          )(error);
        })
      );
  }

  createSimulationPrompts(
    callUuid: string,
    prompts: ISimulationPrompt[]
  ): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .post(`${this.backendUrl}/simulation/createPrompts`, {
        callUuid,
        prompts,
        accountUuid,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`create simulation prompts: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/createPrompts`
          )(error);
        })
      );
  }

  updateSimulationPrompts(
    callUuid: string,
    prompts: ISimulationPrompt[]
  ): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post(`${this.backendUrl}/simulation/updatePrompts`, {
        callUuid,
        prompts,
        accountUuid,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`update simulation prompts: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/updatePrompts`
          )(error);
        })
      );
  }

  generateSttPrompt(callUuid: string): Observable<ISimulationPrompt> {
    return this.http
      .post(
        `${this.backendUrl}/prompt/generateSttPrompt?callUuid=${callUuid}`,
        {}
      )
      .pipe(
        map((result: any) => result),
        tap((res) => {
          const outcome = res ? 'generated' : 'did not generate';
          console.log(`generate STT prompt: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/prompt/generateSttPrompt?callUuid=${callUuid}`
          )(error);
        })
      );
  }

  getSimulationDetails(callUuid: string, moduleUuid: string) {
    return this.http
      .get<any>(
        `${this.backendUrl}/simulation/getSimulationDetails?callUuid=${callUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((res: IFetchSimulationDetails) => {
          // Normalize welcomeMessage to an array
          if (res?.callPersona) {
            const wm = res.callPersona.welcomeMessage;

            if (Array.isArray(wm)) {
              res.callPersona.welcomeMessage = wm;
            } else if (typeof wm === 'string') {
              res.callPersona.welcomeMessage = [wm];
            } else {
              res.callPersona.welcomeMessage = [''];
            }
          }

          return res;
        }),
        tap((res: IFetchSimulationDetails) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`Simulation Details: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/getSimulationDetails?callUuid=${callUuid}&moduleUuid=${moduleUuid}`
          )(error);
        })
      );
  }

  getSectionsWithFacts(callUuid: string) {
    return this.http
      .get<any>(
        `${this.backendUrl}/callFact/findSectionsWithFactsByCallUuid?callUuid=${callUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`Sections with facts: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/callFact/findSectionsWithFactsByCallUuid?callUuid=${callUuid}`
          )(error);
        })
      );
  }

  createSectionWithFacts(
    callUuid: string,
    prompts: ISectionsWithFacts
  ): Observable<any> {
    return this.http
      .post(`${this.backendUrl}/callFact/createSectionWithFacts`, {
        callUuid,
        ...prompts,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`Create sections with facts: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/callFact/createSectionWithFacts`
          )(error);
        })
      );
  }

  updateSectionWithFacts(
    callUuid: string,
    sections: ISectionsWithFacts
  ): Observable<any> {
    return this.http
      .post(`${this.backendUrl}/callFact/updateSectionWithFacts`, {
        callUuid,
        ...sections,
      })
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/callFact/updateSectionWithFacts`
          )(error);
        })
      );
  }

  deleteSection(callUuid: string, sectionUuid: string): Observable<any> {
    return this.http
      .delete(
        `${this.backendUrl}/callFact/deleteSection?callUuid=${callUuid}&sectionUuid=${sectionUuid}`,
        {
          responseType: 'text',
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`Delete section: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/callFact/deleteSection?callUuid=${callUuid}&sectionUuid=${sectionUuid}`
          )(error);
        })
      );
  }

  deleteSectionFact(callUuid: string, factUuid: string): Observable<any> {
    return this.http
      .delete(
        `${this.backendUrl}/callFact/deleteFact?callUuid=${callUuid}&factUuid=${factUuid}`,
        {
          responseType: 'text',
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`Delete section facts: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/callFact/deleteFact?callUuid=${callUuid}&factUuid=${factUuid}`
          )(error);
        })
      );
  }

  getModuleList(accountUuid: string): Observable<IModule[]> {
    if (!accountUuid) {
      throw new Error('accountUuid is required');
    }

    return this.http
      .get<IModule[]>(
        `${this.backendUrl}/module/findByAccountUuid?accountUuid=${accountUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getModuleList: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<IModule[]>(
            `/module/findByAccountUuid?accountUuid=${accountUuid}`
          )(error);
        })
      );
  }

  createModule(name: string): Observable<IModule> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .post<IModule>(`${this.backendUrl}/module/create`, {
        accountUuid,
        name,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'created' : 'creation failed';
          console.log(`createModule: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<IModule>(
            `/module/create`
          )(error);
        })
      );
  }
  createCallBackground(callUuid: string, imageFile: File): Observable<string> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    const formData = new FormData();
    formData.append('call_uuid', callUuid);
    formData.append('account_uuid', accountUuid);
    formData.append('image', imageFile);

    return this.http
      .post(`${this.backendUrl}/call/background/create`, formData, {
        responseType: 'text',
      })
      .pipe(
        tap((s3Url) => {
          console.log(`Background created successfully. S3 URL: ${s3Url}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/call/background/create`
          )(error);
        })
      );
  }

  createCallThumbnail(callUuid: string, imageFile: File): Observable<string> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    const formData = new FormData();
    formData.append('call_uuid', callUuid);
    formData.append('account_uuid', accountUuid);
    formData.append('image', imageFile);

    return this.http
      .post(`${this.backendUrl}/call/thumbnail/create`, formData, {
        responseType: 'text',
      })
      .pipe(
        tap((s3Url) => {
          console.log(`Thumbnail created successfully. S3 URL: ${s3Url}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/call/thumbnail/create`
          )(error);
        })
      );
  }

  resetCallKeywords(
    callUuid: string,
    wordType: CallWordType,
    personaUuid?: string
  ): Observable<any> {
    return this.http
      .post(
        `${this.backendUrl}/word/resetKeywords`,
        {
          callUuid,
          wordType,
          personaUuid,
        },
        {
          responseType: 'text',
        }
      )
      .pipe(
        tap((res) => {
          const outcome = res ? 'reset' : 'reset failed';
          console.log(`resetKeywords: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/word/resetKeywords`
          )(error);
        })
      );
  }

  cloneModuleKeywords(payload: ICloneModuleKeywordsRequest): Observable<any> {
    return this.http
      .post(`${this.backendUrl}/word/cloneModuleKeywords`, payload, {
        responseType: 'text',
      })
      .pipe(
        tap((res) => {
          const outcome = res ? 'cloned' : 'clone failed';
          console.log(`cloneModuleKeywords: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/word/cloneModuleKeywords`
          )(error);
        })
      );
  }

  getWordDetails(
    callUuid: string,
    personaUuid?: string
  ): Observable<IGetWordDetailsResponse> {
    let url = `${this.backendUrl}/word/getWordDetails?callUuid=${callUuid}${
      personaUuid ? `&personaUuid=${personaUuid}` : ''
    }`;

    return this.http.get<IGetWordDetailsResponse>(url).pipe(
      map((result) => result),
      tap((res) => {
        const outcome = res ? 'fetched' : 'did not find';
        console.log(`getWordDetails: ${outcome}`);
      }),
      catchError((error) => {
        return this.errorHandlingService.handleError<IGetWordDetailsResponse>(
          `/word/getWordDetails?callUuid=${callUuid}${
            personaUuid ? `&personaUuid=${personaUuid}` : ''
          }`
        )(error);
      })
    );
  }

  createWords(payload: IWordsRequest): Observable<string> {
    return this.http
      .post(`${this.backendUrl}/word/createWords`, payload, {
        responseType: 'text',
      })
      .pipe(
        tap((res) => {
          console.log(`createWords: created successfully`);
        }),
        catchError((error) => {
          if (error?.status === DUPLICATE_WORD_STATUS) {
            throw error;
          }
          return this.errorHandlingService.handleError<string>(
            `/word/createWords`
          )(error);
        })
      );
  }

  updateWords(payload: IWordsRequest): Observable<string> {
    return this.http
      .post(`${this.backendUrl}/word/updateWords`, payload, {
        responseType: 'text',
      })
      .pipe(
        tap((res) => {
          console.log(`updateWords: updated successfully`);
        }),
        catchError((error) => {
          if (error?.status === DUPLICATE_WORD_STATUS) {
            throw error;
          }
          return this.errorHandlingService.handleError<string>(
            `/word/updateWords`
          )(error);
        })
      );
  }

  deleteWords(payload: IDeleteWordsRequest): Observable<string> {
    return this.http
      .request('DELETE', `${this.backendUrl}/word/deleteWords`, {
        body: payload,
        responseType: 'text',
      })
      .pipe(
        map((res) => res as string),
        tap((res) => {
          console.log(`deleteWords: deleted successfully`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/word/deleteWords`
          )(error);
        })
      );
  }

  saveAsProductDefault(
    payload: ISaveAsProductDefaultRequest
  ): Observable<string> {
    return this.http
      .post(`${this.backendUrl}/word/saveAsProductDefault`, payload, {
        responseType: 'text',
      })
      .pipe(
        tap((res) => {
          console.log(`saveAsProductDefault: saved successfully`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/word/saveAsProductDefault`
          )(error);
        })
      );
  }
}
